/**
 * Design tokens — the single source of truth for Dooglie's visual language,
 * ported from `dooglie-brand-pack-v2`. Nothing visual (the MUI theme,
 * component `sx`, animations) should hardcode a hex / radius / shadow:
 * import from here instead.
 */

export const color = {
  // Brand cobalt
  cobalt: '#3950A0',
  cobaltDeep: '#263780',
  cobaltMid: '#4F67C0',
  cobaltLite: '#7B96E8',
  cobaltGhost: '#D6DEFF',

  // Accents
  signal: '#3FC2C2',
  signalDim: '#2EA8A8',
  fire: '#FF5C3A',
  gold: '#F5C842',

  // Ink & surface
  ink: '#0C0F1E',
  inkMid: '#1A2040',
  surface: '#F4F6FF',
  surfaceMid: '#E8EBF7',

  // Neutral scale
  neutral50: '#F8F9FC',
  neutral100: '#EFF1F9',
  neutral200: '#D8DCEC',
  neutral400: '#9AA3C0',
  neutral600: '#5C6480',
  neutral800: '#2A2F47',
  neutral900: '#141829',

  white: '#FFFFFF',
} as const;

/** Semantic roles — the only place status/intent colours are named. */
export const semantic = {
  success: '#2E9E5B',
  warning: color.gold,
  error: color.fire,
  /** Live / recording / "Dooglie is on" — always paired with the pulse. */
  live: color.signal,
} as const;

/** Backend widget colour names → hex. Replaces the duplicated COLOR_TOKENS maps. */
export const widgetColor = {
  green: semantic.success,
  amber: semantic.warning,
  red: semantic.error,
} as const;

/**
 * Backend `WidgetCategory` → header pill. The pill background carries the
 * colour (a soft tint so the label reads in ink); the label text itself stays
 * neutral. Rendered in place of the old coloured dot in the card header.
 */
export const categoryBadge = {
  risk: { bg: 'rgba(255, 92, 58, 0.16)', label: 'Risk' },
  opportunity: { bg: 'rgba(46, 158, 91, 0.16)', label: 'Opportunity' },
  caution: { bg: 'rgba(245, 200, 66, 0.26)', label: 'Caution' },
  info: { bg: color.cobaltGhost, label: 'Info' },
} as const;

/**
 * Backend `WidgetCategory` → inline body-highlight text colour. The Markdown
 * renderer maps `[risk]…[/risk]`-style tags to these colours. Same vocabulary
 * as the header pill so highlights flag meaning, not raw colour.
 */
export const highlightColor = {
  risk: semantic.error,
  opportunity: semantic.success,
  caution: '#B8860B', // dark goldenrod — gold reads too faint as text on white
  info: color.cobalt,
} as const;

export const radius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 20,
  pill: 999,
} as const;

export const shadow = {
  card: '0 2px 12px rgba(57,80,160,0.10), 0 1px 3px rgba(0,0,0,0.08)',
  panel: '0 8px 32px rgba(57,80,160,0.14), 0 2px 8px rgba(0,0,0,0.06)',
  deep: '0 24px 64px rgba(57,80,160,0.18), 0 8px 24px rgba(0,0,0,0.12)',
} as const;

export const motion = {
  easeOutExpo: 'cubic-bezier(0.16, 1, 0.3, 1)',
  easeSpring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

export const font = {
  /** Convergence ships one weight (400): never bold it. */
  display: "'Convergence', system-ui, sans-serif",
  body: "'DM Sans Variable', system-ui, -apple-system, 'Segoe UI', sans-serif",
  /** Literal code only — never a label. */
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
} as const;

/** 4px-based spacing scale (px). MUI's own `spacing()` factor stays at 8px. */
export const space = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
} as const;

/** Thin custom scrollbar, matched to the brand surface. */
export const scrollbarSx = {
  // Always reserve the scrollbar gutter so content doesn't shift sideways the
  // moment the scrollbar appears (e.g. when a widget expands the list past the
  // viewport).
  scrollbarGutter: 'stable',
  '&::-webkit-scrollbar': { width: 6, height: 6 },
  '&::-webkit-scrollbar-thumb': {
    background: color.neutral200,
    borderRadius: radius.sm,
  },
} as const;

/** Signal-teal "live" pulse — the keyframe `dooglie-pulse` is defined globally in the theme. */
export const livePulseSx = {
  animation: 'dooglie-pulse 2s ease-in-out infinite',
} as const;

/**
 * Cobalt "syncing" pulse — the same beat as `livePulseSx` in brand cobalt, so
 * the header can show "recording" and "syncing" side by side without the two
 * reading as the same state. Keyframe `dooglie-pulse-sync` lives in the theme.
 */
export const syncPulseSx = {
  animation: 'dooglie-pulse-sync 2s ease-in-out infinite',
} as const;
